import React, { useEffect, useRef } from 'react';

import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';

import { ColumnState, GridApi, ILoadingOverlayParams, RowNode } from 'ag-grid-community';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import { isEmpty, isNil } from 'lodash';
import {
    DEFAULT_COLUMN_DEFINITIONS,
    DEFAULT_EXCEL_EXPORT_CONFIG,
    DEFAULT_GROUP_DISPLAY_TYPE,
    DEFAULT_ROW_SELECTION,
    DEFAULT_STATUS_BAR
} from 'src/constants/agGrid';

import './BaseAgGrid.scss';

/**
 * Custom loading overlay component using Meridian Loader
 */
const CustomLoadingOverlay: React.FC<ILoadingOverlayParams> = () => {
    return (
        <div className="ag-overlay-loading-center">
            <Row spacing="200" alignmentVertical="center">
                <Loader size="small" />
                <span className="ag-overlay-loading-text">Loading...</span>
            </Row>
        </div>
    );
};

export interface IBaseAgGridProps extends AgGridReactProps {
    filterText?: string;
    sortModel?: ColumnState[];
    persistedSelectedRowNodes?: RowNode[];
    setAgGridApi?: React.Dispatch<React.SetStateAction<GridApi | undefined>>;
    suppressSizeColumnsToFit?: boolean;
}

export const BaseAgGrid = ({
    columnDefs,
    rowData,
    filterText = '',
    sortModel,
    persistedSelectedRowNodes,
    setAgGridApi,
    suppressSizeColumnsToFit = false,
    ...rest
}: IBaseAgGridProps) => {
    const gridRef = useRef<AgGridReact>(null);

    const updateQuickFilter = () => {
        if (gridRef.current!.api && !isNil(filterText)) {
            gridRef.current!.api.setGridOption('quickFilterText', filterText);
        }
    };

    const onGridReadyHandler = () => {
        if (!suppressSizeColumnsToFit) {
            gridRef.current!.api.sizeColumnsToFit();
        }
        if (sortModel) gridRef.current!.api.applyColumnState({ state: sortModel });
        if (!isEmpty(persistedSelectedRowNodes)) {
            persistedSelectedRowNodes?.forEach((rowNode) => {
                if (rowNode.id != null) {
                    gridRef.current!.api.getRowNode(rowNode.id)!.setSelected(true);
                }
            });
        }
        if (gridRef.current) {
            setAgGridApi?.(gridRef.current.api);

            updateQuickFilter();
        }
    };

    useEffect(updateQuickFilter, [filterText]);

    const onGridSizeChanged = () => {
        if (!suppressSizeColumnsToFit) {
            gridRef.current?.api.sizeColumnsToFit();
        }
    };

    return (
        <div className="base-ag-grid ag-theme-alpine">
            <AgGridReact
                ref={gridRef}
                defaultColDef={DEFAULT_COLUMN_DEFINITIONS}
                columnDefs={columnDefs}
                rowData={rowData}
                statusBar={DEFAULT_STATUS_BAR}
                excelStyles={DEFAULT_EXCEL_EXPORT_CONFIG}
                onGridReady={onGridReadyHandler}
                onGridSizeChanged={onGridSizeChanged}
                rowSelection={DEFAULT_ROW_SELECTION}
                singleClickEdit={true}
                accentedSort={true}
                suppressRowClickSelection={true}
                enableRangeSelection={true}
                loadingOverlayComponent={CustomLoadingOverlay}
                {...rest}
            />
        </div>
    );
};
